import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { fmtDate } from '@dcc-erp/shared';
import type { ArInvoiceVoid } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useApproveArInvoiceVoidStep,
  useArInvoiceVoid,
  useRejectArInvoiceVoidStep,
  useReturnArInvoiceVoid,
  useSubmitArInvoiceVoid,
} from '../../lib/queries';
import { AppModal } from '../AppModal';
import { ApprovalTimeline, type ApprovalTimelineRow } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

// The wire `ArInvoiceVoid` list type does not formally declare the engine
// detail fields (the materialized approval chain + the submit/return/approve
// audit stamps); the API's findVoid returns them (mirrors ar_credit_note
// detail). Narrow them here for the panel — same idiom CreditNoteList uses for
// the enriched list row.
type ArInvoiceVoidDetail = ArInvoiceVoid & {
  approvals?: ApprovalTimelineRow[];
  submittedAt?: string | Date | null;
  approvedAt?: string | Date | null;
  returnedReason?: string | null;
};

interface Props {
  companyId: string;
  /** The list row that opened this panel — used for the header before the full
   *  detail (with the approvals chain) resolves. */
  void: ArInvoiceVoidDetail;
  onClose: () => void;
}

// AR invoice-void central-engine 4-eyes panel. Mirrors ArCreditNoteApprovalPanel
// exactly: re-fetch the full detail for the materialized approvals chain, render
// the shared ApprovalTimeline, and drive submit/approve/return/reject through
// the doc-type-agnostic DecisionModal. Final-step approval auto-posts the
// reversing JV server-side and flips the invoice → VOID (Design-Z: the request's
// accounting `status` flips to POSTED).
export function ArInvoiceVoidApprovalPanel({ companyId, void: voidRow, onClose }: Props) {
  const { data: detailRaw } = useArInvoiceVoid(companyId, voidRow.id);
  const detail = detailRaw as ArInvoiceVoidDetail | undefined;
  const view = detail ?? voidRow;

  const submitMut = useSubmitArInvoiceVoid(companyId);
  const approveMut = useApproveArInvoiceVoidStep(companyId);
  const rejectMut = useRejectArInvoiceVoidStep(companyId);
  const returnMut = useReturnArInvoiceVoid(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;

  const approvalStatus = view.approvalStatus ?? null;
  const currentStep = view.currentStep ?? 0;
  // The materialized approval chain only exists on the re-fetched detail.
  const approvals = detail?.approvals ?? [];
  const totalSteps = approvals.length;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';
  const customerName = detail?.customerName ?? voidRow.customerName ?? null;

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(view.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          id: view.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['ar-invoice-voids', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <AppModal
      title={`สายอนุมัติใบยกเลิก — ${view.voucherNo}`}
      {...(customerName ? { subtitle: customerName } : {})}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="ar.invoice.void">
              <button
                className="btn primary"
                disabled={submitBusy}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="ar.invoice.void.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                onClick={() => setShowDecide(true)}
              >
                พิจารณา
              </button>
            </Can>
          )}
        </>
      }
    >
      {/* RETURNED reason banner (resubmit context) */}
      {approvalStatus === 'RETURNED' && view.returnedReason && (
        <div
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 12,
          }}
          data-testid="ar-void-returned-reason"
        >
          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
          {view.returnedReason}
        </div>
      )}

      {/* awaiting-step hint */}
      {isPending && (
        <div className="meta" style={{ marginBottom: 12 }}>
          <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
        </div>
      )}

      <ApprovalTimeline
        approvals={approvals}
        isActive={isPending}
        currentStep={currentStep}
        emptyText="ยังไม่ส่งอนุมัติ"
        leadLine={
          view.submittedBy ? (
            <div className="meta">
              ส่งอนุมัติโดย {view.submittedBy}
              {view.submittedAt ? ` · ${fmtDate(view.submittedAt)}` : ''}
            </div>
          ) : undefined
        }
        trailLine={
          view.status === 'POSTED' && view.approvedBy ? (
            <div className="meta">
              อนุมัติโดย {view.approvedBy}
              {view.approvedAt ? ` · ${fmtDate(view.approvedAt)}` : ''}
            </div>
          ) : undefined
        }
      />

      {actionError && (
        <div
          style={{ color: 'var(--negative)', fontSize: 14, marginTop: 10 }}
          data-testid="ar-void-action-error"
        >
          {actionError}
        </div>
      )}

      {showDecide && (
        <DecisionModal
          voucherNo={view.voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาใบยกเลิก"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </AppModal>
  );
}
